import { useEffect, useMemo, useState } from "react";

import clsx from "clsx";
import { cloneDeep, isEqual } from "lodash";

import { GenericTooltip } from "@components/tooltip-container";
import { useUser } from "@components/user-context";
import {
  DangerButton,
  ModalPanel,
  PrimaryButton,
  SecondaryButton,
  Spinner,
  TertiaryButton,
  TextInput,
} from "@components/v2";
import { usePrettyLogEventPage } from "@components/v2/analytics";
import { useQuickFilters } from "@features/quick-filters";
import { PLATFORM_BREAKDOWN_ID } from "@features/sales-v4/constants";
import { useSalesV4Context } from "@features/sales-v4/hooks";
import { useUserAttributionSettings } from "@hooks/use-user-attribution-settings";
import { type SalesTableReportV3BreakdownsFilterInput } from "@north-beam/nb-client-side";
import {
  type CompareDateRangeV2,
  type DateRangeChoiceV4,
  type FixedDateInterval,
  type ITimeGranularityV2,
} from "@north-beam/nb-common";
import { Modal } from "@shared/modal";

import { Breakdowns } from "./breakdowns";
import { Dashboards } from "./dashboards";
import { Dates } from "./dates";
import { Filters } from "./filters";
import { SyncConfirmationModal } from "./sync-confirmation-modal";

const DEFAULT_DATE_RANGE = "last7Days";
const DEFAULT_COMPARE_DATE_RANGE: CompareDateRangeV2 = {
  value: "previousPeriod",
};
const DEFAULT_TIME_GRANULARITY: ITimeGranularityV2 = "daily";
const DEFAULT_BREAKDOWN_IDS = [PLATFORM_BREAKDOWN_ID, "date"];
const DEFAULT_FILTERS: SalesTableReportV3BreakdownsFilterInput[] = [];

interface Props {
  isOpen: boolean;
  onClose: () => void;
}

export const AdvancedCustomizationModal = (props: Props) => {
  return (
    <Modal
      contentClassName="max-w-[1264px] max-h-[75%] h-auto flex flex-col"
      bodyClassName="flex flex-col overflow-hidden"
      variant="v2"
      {...props}
    >
      <Content onClose={props.onClose} />
    </Modal>
  );
};

const Content = ({ onClose }: { onClose: () => void }) => {
  const { user } = useUser();
  const { accountingMode } = useUserAttributionSettings();
  const {
    selectedQuickFilter,
    tableStates,
    chartState,
    setSelectedBreakdownIds,
    setDateRange,
    setTimeGranularity,
    setCompareDateRange,
    setTableStates,
    setChartState,
    applyQuickFilter,
  } = useSalesV4Context();
  const {
    quickFilters,
    saving,
    deleting,
    reordering,
    syncing,
    upsertQuickFilter,
    deleteQuickFilter,
    reorderQuickFilters,
    syncQuickFilters,
  } = useQuickFilters();
  const prettyLogEventPage = usePrettyLogEventPage();

  const [localDateRange, setLocalDateRange] = useState<
    FixedDateInterval | DateRangeChoiceV4
  >(DEFAULT_DATE_RANGE);
  const [localCompareDateRange, setLocalCompareDateRange] =
    useState<CompareDateRangeV2>(DEFAULT_COMPARE_DATE_RANGE);
  const [localTimeGranularity, setLocalTimeGranularity] =
    useState<ITimeGranularityV2>(DEFAULT_TIME_GRANULARITY);
  const [localSelectedBreakdownIds, setLocalSelectedBreakdownIds] = useState<
    string[]
  >(DEFAULT_BREAKDOWN_IDS);
  const [localSelectedFilters, setLocalSelectedFilters] =
    useState<SalesTableReportV3BreakdownsFilterInput[]>(DEFAULT_FILTERS);

  const [dashboards, setDashboards] = useState(quickFilters);
  const [dashboardName, setDashboardName] = useState("");
  const [selectedDashboardId, setSelectedDashboardId] = useState<string | null>(
    null,
  );

  const [isSyncConfirmationModalOpen, setIsSyncConfirmationModalOpen] =
    useState(false);
  const [isSynced, setIsSynced] = useState(false);

  useEffect(() => {
    setDashboards(quickFilters);
  }, [quickFilters]);

  const isViewChanged = useMemo(() => {
    const quickFilter = quickFilters.find((e) => e.id === selectedDashboardId);

    if (quickFilter) {
      return !(
        isEqual(localDateRange, quickFilter.config.dateRange) &&
        isEqual(localCompareDateRange, quickFilter.config.compareDateRange) &&
        isEqual(localTimeGranularity, quickFilter.config.timeGranularity) &&
        isEqual(localSelectedBreakdownIds, quickFilter.config.breakdownIds) &&
        isEqual(localSelectedFilters, quickFilter.config.breakdownFilters) &&
        isEqual(dashboardName, quickFilter.name)
      );
    } else {
      return !(
        isEqual(localDateRange, DEFAULT_DATE_RANGE) &&
        isEqual(localCompareDateRange, DEFAULT_COMPARE_DATE_RANGE) &&
        isEqual(localTimeGranularity, DEFAULT_TIME_GRANULARITY) &&
        isEqual(localSelectedBreakdownIds, DEFAULT_BREAKDOWN_IDS) &&
        isEqual(localSelectedFilters, DEFAULT_FILTERS) &&
        isEqual(dashboardName, "")
      );
    }
  }, [
    selectedDashboardId,
    localDateRange,
    localCompareDateRange,
    localTimeGranularity,
    localSelectedBreakdownIds,
    localSelectedFilters,
    dashboardName,
    selectedDashboardId,
  ]);

  const isDashboardNameInvalid = useMemo(() => {
    return dashboards
      .filter((e) => e.id !== selectedDashboardId)
      .map((e) => e.name)
      .includes(dashboardName);
  }, [dashboardName, dashboards, selectedDashboardId]);

  return (
    <ModalPanel className="flex flex-col overflow-hidden gap-6">
      <div className="px-0.5">
        <div className="font-semibold text-base mb-2">
          Advanced Customization
        </div>
        <p className="text-xs text-v2-secondary m-0">
          Select custom breakdowns and filter your view. If you would like to
          save your view to use again, simply give it a name.
        </p>
      </div>
      <div className="flex gap-6 overflow-hidden">
        <div className="flex-1 flex flex-col gap-6 overflow-hidden px-0.5">
          <div>
            <div className="text-xs font-semibold mb-2">Dashboard Name</div>
            <div>
              <TextInput
                className="w-[calc(384px+8px)]"
                size="md"
                placeholder="New Dashboard Name"
                value={dashboardName}
                onChange={(e) => {
                  setDashboardName(e.target.value);
                }}
              />
              {isDashboardNameInvalid && (
                <div className="text-2xs mt-1 text-v2-action-negative-hover">
                  {dashboardName} is already taken
                </div>
              )}
            </div>
          </div>
          <Dates
            dateRange={localDateRange}
            compareDateRange={localCompareDateRange}
            timeGranularity={localTimeGranularity}
            onChangeDateRange={(val) => {
              setLocalDateRange(val);
            }}
            onChangeCompareDateRange={(val) => {
              setLocalCompareDateRange(val);
            }}
            onChangeTimeGranularity={(val) => {
              setLocalTimeGranularity(val);
            }}
            timezone={user.timezone}
          />
          <Breakdowns
            selection={localSelectedBreakdownIds}
            onChange={setLocalSelectedBreakdownIds}
          />
          <div className="flex-1 flex flex-col overflow-hidden">
            <Filters
              selection={localSelectedFilters}
              onChange={setLocalSelectedFilters}
            />
          </div>
          {selectedDashboardId && (
            <div>
              <GenericTooltip
                noInfoCircle
                content="At least one dashboard is required to be present"
                showTooltip={quickFilters.length < 2}
                placement="top"
              >
                <DangerButton
                  disabled={
                    quickFilters.length < 2 || deleting || reordering || saving
                  }
                  onClick={async () => {
                    await deleteQuickFilter(selectedDashboardId);

                    if (selectedQuickFilter.id === selectedDashboardId) {
                      applyQuickFilter(
                        quickFilters.filter(
                          (e) => e.id !== selectedDashboardId,
                        )[0],
                      );
                    }

                    onClose();
                  }}
                >
                  {deleting && <Spinner />}
                  DELETE VIEW
                </DangerButton>
              </GenericTooltip>
            </div>
          )}
        </div>
        <div className="flex flex-col w-[200px] overflow-hidden">
          <Dashboards
            dashboards={dashboards}
            selectedDashboardId={selectedDashboardId}
            reordering={reordering}
            onSelect={(val) => {
              const quickFilter = quickFilters.find((e) => e.id === val);

              if (quickFilter) {
                setSelectedDashboardId(quickFilter.id);
                setDashboardName(quickFilter.name);

                setLocalDateRange(quickFilter.config.dateRange);
                setLocalCompareDateRange(quickFilter.config.compareDateRange);
                setLocalTimeGranularity(
                  quickFilter.config.timeGranularity ??
                    DEFAULT_TIME_GRANULARITY,
                );
                setLocalSelectedBreakdownIds(quickFilter.config.breakdownIds);
                setLocalSelectedFilters(quickFilter.config.breakdownFilters);
              }
            }}
            onReorder={(val) => {
              setDashboards(val);
              setTimeout(() => {
                reorderQuickFilters(val.map((e) => e.id));
              }, 0);
            }}
          />
        </div>
      </div>
      <div className="flex items-center justify-between">
        <TertiaryButton
          disabled={!isViewChanged && selectedDashboardId === null}
          onClick={() => {
            setLocalDateRange(DEFAULT_DATE_RANGE);
            setLocalCompareDateRange(DEFAULT_COMPARE_DATE_RANGE);
            setLocalTimeGranularity(DEFAULT_TIME_GRANULARITY);
            setLocalSelectedBreakdownIds(DEFAULT_BREAKDOWN_IDS);
            setLocalSelectedFilters(DEFAULT_FILTERS);
            setDashboardName("");
            setSelectedDashboardId(null);
          }}
        >
          RESET
        </TertiaryButton>
        <div className="flex items-center gap-2 text-xs">
          {isSynced ? (
            <div
              className={clsx(
                "flex items-center justify-center gap-2 text-v2-action-positive-hover mr-2",
              )}
            >
              <i className="fa-solid fa-check"></i>
              Synced successfully
            </div>
          ) : (
            <>
              <SecondaryButton
                disabled={syncing}
                onClick={() => {
                  setIsSyncConfirmationModalOpen(true);
                }}
              >
                {syncing && <Spinner />}
                SYNC SAVED VIEWS
              </SecondaryButton>
              <SyncConfirmationModal
                isOpen={isSyncConfirmationModalOpen}
                onClose={() => {
                  setIsSyncConfirmationModalOpen(false);
                }}
                onConfirm={async () => {
                  setIsSyncConfirmationModalOpen(false);
                  await syncQuickFilters();
                  setIsSynced(true);
                }}
              />
            </>
          )}
          {dashboardName && isViewChanged ? (
            <GenericTooltip
              noInfoCircle
              content={
                !dashboardName
                  ? "Name your dashboard to save"
                  : selectedDashboardId
                  ? "Update selected dashboard"
                  : "Create a new dashboard"
              }
              placement="top"
            >
              <PrimaryButton
                disabled={
                  localSelectedBreakdownIds.length === 0 ||
                  isDashboardNameInvalid ||
                  saving ||
                  reordering ||
                  deleting
                }
                onClick={async () => {
                  const result = await upsertQuickFilter({
                    id: selectedDashboardId ?? undefined,
                    name: dashboardName,
                    config: {
                      accountingMode,
                      dateRange: localDateRange,
                      compareDateRange: localCompareDateRange,
                      timeGranularity: localTimeGranularity,
                      breakdownIds: localSelectedBreakdownIds,
                      breakdownFilters: localSelectedFilters,
                      selectedTab: "breakdown",
                      chartMetricIds: chartState.metrics.map((e) => e.metricId),
                      alignAxesScale: chartState.alignAxesScale,
                      tableMetricIds: tableStates[0].selectedMetricIds,
                      sorting: tableStates[0].sorting,
                    },
                  });

                  if (result.data?.upsertQuickFilter) {
                    applyQuickFilter(result.data?.upsertQuickFilter);
                  }

                  onClose();
                }}
              >
                {saving && <Spinner />}
                SAVE
              </PrimaryButton>
            </GenericTooltip>
          ) : (
            <GenericTooltip
              noInfoCircle
              content={
                isViewChanged
                  ? "Name your dashboard to save"
                  : "Make any changes to view them"
              }
              placement="top"
            >
              <SecondaryButton
                disabled={
                  (!isViewChanged && selectedDashboardId === null) ||
                  reordering ||
                  deleting
                }
                onClick={() => {
                  const selectedQuickFilter = quickFilters.find(
                    (e) => e.id === selectedDashboardId,
                  );

                  if (selectedQuickFilter) {
                    applyQuickFilter({
                      ...selectedQuickFilter,
                      config: {
                        ...selectedQuickFilter.config,
                        dateRange: localDateRange,
                        compareDateRange: localCompareDateRange,
                        timeGranularity: localTimeGranularity,
                        breakdownIds: localSelectedBreakdownIds,
                        breakdownFilters: localSelectedFilters,
                      },
                    });
                  } else {
                    setDateRange(localDateRange);
                    setCompareDateRange(localCompareDateRange);
                    setTimeGranularity(localTimeGranularity);
                    setSelectedBreakdownIds(localSelectedBreakdownIds);
                    setTableStates([
                      {
                        ...tableStates[0],
                        selectedTab: "breakdown",
                        searchText: "",
                        selectedBreakdownId: localSelectedBreakdownIds[0],
                        breakdownFilters: localSelectedFilters,
                        breakdownSelection: [],
                        campaignSelection: [],
                        adsetSelection: [],
                        adSelection: [],
                      },
                    ]);
                    setChartState({
                      ...chartState,
                      metrics: chartState.metrics.map((metric) => ({
                        ...metric,
                        filters: cloneDeep(localSelectedFilters),
                      })),
                    });
                  }

                  prettyLogEventPage({
                    domain: "Sales Attribution",
                    action: "onClick",
                    additionalActionInfo: "View Sales Attribution",
                    data: {
                      pageContext: {
                        selectedMetrics: tableStates[0].selectedMetricIds,
                        selectedBreakdownIds: localSelectedBreakdownIds,
                        selectedFilters: localSelectedFilters,
                        dateRange: localDateRange,
                        compareDateRange: localCompareDateRange,
                        accountingMode,
                      },
                    },
                  });
                  onClose();
                }}
              >
                VIEW
              </SecondaryButton>
            </GenericTooltip>
          )}
        </div>
      </div>
    </ModalPanel>
  );
};
